import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Header from "../component/Header";
import Sidebar from "../component/Sidebar";
import "../css/sidebar.css";
import "../css/modern-modal.css";
import "../css/setTargetModal.css";
import "rsuite/dist/rsuite.min.css";
import { Placeholder, SelectPicker, Whisper, Tooltip } from "rsuite";
import { motion, AnimatePresence } from "framer-motion";
import fivePaisaWebSocketService from "../services/fivePaisaWebSocketService";
import fivePaisaOptionGreeksWebSocketService from "../services/fivePaisaOptionGreeksWebSocketService";
import marketSnapshotService from "../services/marketSnapshotService";
import { FiBookmark, FiShoppingBag, FiTrendingUp } from "react-icons/fi";
import ArrowTrendUp from "../images/arrow-trend-up.svg";
import ArrowTrendDown from "../images/arrow-trend-down.svg";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getCurrentLanguage, t } from "../i18n";
import {
  placeOrderViaPms,
  normalizePlaceOrderResponse,
  isFivePaisaBroker,
  runPlaceOrderAcceptedFlow,
} from "../services/placeOrderService";
import StockCandleModal from "../component/StockCandleModal";
import SelectWatchlistModal from "../component/SelectWatchlistModal";

// const API_BASE = "https://beta.infomanav.in/keep/zerodhaAPI/api.php";
const API_BASE = "https://pmsapi.infomanav.in/api/v1/FNO";

const getStoredBrokerType = () => {
  const sharedData = JSON.parse(localStorage.getItem("sharedData") || "null");
  return String(
    sharedData?.broker_type || localStorage.getItem("broker_type") || "",
  ).toLowerCase();
};

function AngelOneFNOComingSoon() {
  return (
    <>
      <Header tabName="FNO-360" />
      <div className="wrapper__page pb-5">
        <Sidebar />
        <div className="content_col" />
      </div>

      <div
        className="modal fade show d-block"
        role="dialog"
        aria-modal="true"
        tabIndex="-1"
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.35)",
          pointerEvents: "none",
        }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ pointerEvents: "auto" }}>
          <div className="modal-content">
            <div className="modal-body text-center py-4">
              <h5 className="mb-0">Coming soon</h5>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function FNO() {
  if (getStoredBrokerType() === "angelone") {
    return <AngelOneFNOComingSoon />;
  }

  return <FNOContent />;
}

const fnoLegActionBtnBase = {
  border: "none",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 4,
  flexShrink: 0,
};

const STRIKE_WINDOW_RADIUS = 20;
const SCROLL_EXTEND_STRIKES = 10;

const toMarketFeedExchange = (exchange) => {
  const raw = String(exchange || "").toUpperCase();
  if (raw.startsWith("B")) return "B";
  if (raw.startsWith("M")) return "M";
  return "N";
};

const toMarketFeedExchangeType = (exchangeType) => {
  const raw = String(exchangeType || "").toUpperCase();
  return raw.startsWith("D") ? "D" : "C";
};

const EXPIRY_MONTH_ABBREVS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

/** `2026-05-12` → `12 MAY'26` for expiry dropdown; unknown values returned unchanged. */
function formatExpirySelectLabel(exp) {
  const s = String(exp ?? "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return s;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(y) || mo < 1 || mo > 12 || day < 1 || day > 31) return s;
  return `${day} ${EXPIRY_MONTH_ABBREVS[mo - 1]}'${String(y).slice(-2)}`;
}

function buildOptionChainRows(contracts) {
  const list = Array.isArray(contracts) ? contracts : [];
  const ceData = list.filter((x) => x?.ScripType === "CE");
  const peData = list.filter((x) => x?.ScripType === "PE");
  const strikeMap = {};

  ceData.forEach((item) => {
    const strike = Number(item.StrikeRate);
    if (!Number.isFinite(strike)) return;
    if (!strikeMap[strike]) {
      strikeMap[strike] = { strike, ce: null, pe: null };
    }
    strikeMap[strike].ce = item;
  });

  peData.forEach((item) => {
    const strike = Number(item.StrikeRate);
    if (!Number.isFinite(strike)) return;
    if (!strikeMap[strike]) {
      strikeMap[strike] = { strike, ce: null, pe: null };
    }
    strikeMap[strike].pe = item;
  });

  const rows = Object.values(strikeMap).sort((a, b) => a.strike - b.strike);
  return rows;
}

function rowToFeedItems(row) {
  const out = [];
  const pushLeg = (leg) => {
    if (!leg) return;
    const scrip = Number(leg.ScripCode ?? leg.scripcode ?? 0);
    if (!scrip) return;
    const Exch = toMarketFeedExchange(leg.Exch);
    const ExchType = toMarketFeedExchangeType(leg.ExchType);
    if (!Exch || !ExchType) return;
    out.push({ Exch, ExchType, ScripCode: scrip });
  };
  pushLeg(row.ce);
  pushLeg(row.pe);
  return out;
}

function feedKey(item) {
  return `${item.Exch}|${item.ExchType}|${item.ScripCode}`;
}

/** OpenAPI greeks instrument id: og + ScripCode (e.g. og41757). */
function scripToOptionGreekInstrument(scripCode) {
  const n = Number(scripCode);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `og${n}`;
}

function displayRowsToGreekInstrumentIds(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    rowToFeedItems(row).forEach((item) => {
      if (item.ExchType !== "D") return;
      const id = scripToOptionGreekInstrument(item.ScripCode);
      if (id) map.set(id, id);
    });
  });
  return Array.from(map.keys());
}

function getAtmIndex(rows, underlyingLtp) {
  if (!rows.length) return -1;
  if (!Number.isFinite(underlyingLtp)) {
    return Math.floor((rows.length - 1) / 2);
  }
  let bestIndex = 0;
  let bestDiff = Number.POSITIVE_INFINITY;
  rows.forEach((row, idx) => {
    const diff = Math.abs(Number(row.strike) - underlyingLtp);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = idx;
    }
  });
  return bestIndex;
}

function FNOContent() {
  const [tabName] = useState("FNO-360");
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());
  const tr = (key) => t(key, currentLanguage);

  const sharedData = JSON.parse(localStorage.getItem("sharedData") || "null");
  const isGrowBroker =
    String(
      sharedData?.broker_type || localStorage.getItem("broker_type") || "",
    ).toLowerCase() === "grow";
  const activeBrokerFlag = isGrowBroker ? "grow" : "5paisa";
  const pmsApiBaseUrl =
    process.env.REACT_APP_PMS_API_BASE_URL || "https://pmsapi.infomanav.in";

  const [searchText, setSearchText] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [searchedSymbols, setSearchedSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState("NIFTY");
  const [expiries, setExpiries] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState("");
  const [contracts, setContracts] = useState([]);
  const [allChainRows, setAllChainRows] = useState([]);
  const [sliceStart, setSliceStart] = useState(0);
  const [sliceEnd, setSliceEnd] = useState(0);
  /** Cash index spot LTP (SPOT_Data); drives ATM when available. */
  const [spotLtp, setSpotLtp] = useState(null);
  /** Nearest futures / underlying XX contract LTP for FUT display. */
  const [futuresLtp, setFuturesLtp] = useState(null);
  const [spotData, setSpotData] = useState(null);
  const [isLiveLoading, setIsLiveLoading] = useState(true);
  const [, setLiveTickVersion] = useState(0);
  const [, setSnapshotVersion] = useState(0);

  const liveByTokenRef = useRef({});
  const wsPriceUnsubRef = useRef(null);
  const wsErrorUnsubRef = useRef(null);
  const subscribedFeedItemsRef = useRef([]);
  const subscribedGreekIdsRef = useRef([]);
  const optionGreeksByTokenRef = useRef({});
  const wsGreeksUnsubRef = useRef(null);
  const wsGreeksErrorUnsubRef = useRef(null);
  const subscribedSpotRef = useRef(null);
  const subscribedFuturesRef = useRef(null);
  const spotTokenRef = useRef(null);
  const futuresTokenRef = useRef(null);
  const scrollRafRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const atmRowRef = useRef(null);
  const shouldAutoScrollToAtmRef = useRef(false);
  const pendingLiveAtmCenterRef = useRef(false);
  const snapshotByTokenRef = useRef({});
  const snapshotPollTimerRef = useRef(null);
  const [atmPillDock, setAtmPillDock] = useState("none");
  const [greeksEnabled, setGreeksEnabled] = useState(false);
  const [, setGreeksTickVersion] = useState(0);
  const [orderType, setOrderType] = useState("Limit Order");
  const [isPreview, setIsPreview] = useState(false);
  const [showFoActivationModal, setShowFoActivationModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [watchlistModalStock, setWatchlistModalStock] = useState(null);
  const [buySellModalDetails, setBuySellModalDetails] = useState({
    FullName: "",
    Symbol: "",
    Type: "Buy",
    OrderQty: 1,
    LotSize: 1,
    LimitPrice: 0,
    order: null,
  });

  const thStyle = {
    padding: 12,
    textAlign: "center",
    borderBottom: "1px solid #cddcf0",
    color: "#334155",
    fontSize: 11,
    fontWeight: 700,
    background: "#dfeaf8",
  };

  const tdStyle = {
    padding: 12,
    textAlign: "center",
    borderBottom: "1px solid #d8e4f4",
    color: "#0f172a",
    fontSize: 11,
  };

  const atmPillSpanStyle = {
    padding: "2px 8px",
    borderRadius: 999,
    background: "#1d4ed8",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.3,
    whiteSpace: "nowrap",
    boxShadow: "0 4px 10px rgba(29, 78, 216, 0.25)",
  };

  const underlyingContract = useMemo(() => {
    return (contracts || []).find((c) => c?.ScripType === "XX") || null;
  }, [contracts]);

  /** MCX commodities: no separate cash spot in scripmaster; use XX futures only. */
  const isMcxChain = useMemo(
    () => toMarketFeedExchange(underlyingContract?.Exch) === "M",
    [underlyingContract],
  );

  const atmReferencePrice = useMemo(() => {
    if (isMcxChain) {
      return Number.isFinite(futuresLtp) ? futuresLtp : null;
    }
    if (Number.isFinite(spotLtp)) return spotLtp;
    if (Number.isFinite(futuresLtp)) return futuresLtp;
    return null;
  }, [isMcxChain, spotLtp, futuresLtp]);

  const atmIndex = useMemo(
    () => getAtmIndex(allChainRows, atmReferencePrice),
    [allChainRows, atmReferencePrice],
  );

  const displayRows = useMemo(() => {
    return allChainRows.slice(sliceStart, sliceEnd).map((row, idx) => ({
      ...row,
      isATM: sliceStart + idx === atmIndex,
    }));
  }, [allChainRows, sliceStart, sliceEnd, atmIndex]);

  useEffect(() => {
    initializeDefaultNiftyChain();
  }, []);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      setCurrentLanguage(event?.detail?.language || getCurrentLanguage());
    };
    window.addEventListener("app-language-change", handleLanguageChange);
    return () =>
      window.removeEventListener("app-language-change", handleLanguageChange);
  }, []);

  useEffect(() => {
    if (!contracts.length) {
      setAllChainRows([]);
      setSliceStart(0);
      setSliceEnd(0);
      setSpotData(null);
      setSpotLtp(null);
      setFuturesLtp(null);
      spotTokenRef.current = null;
      futuresTokenRef.current = null;
      return;
    }
    const rows = buildOptionChainRows(contracts);
    setAllChainRows(rows);
    if (!rows.length) {
      setSliceStart(0);
      setSliceEnd(0);
      return;
    }
    const center = getAtmIndex(rows, null);
    const start = Math.max(0, center - STRIKE_WINDOW_RADIUS);
    const end = Math.min(rows.length, center + STRIKE_WINDOW_RADIUS + 1);
    setSliceStart(start);
    setSliceEnd(end);
  }, [contracts]);

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0.00";
    const number = parseFloat(num);
    if (isNaN(number)) return "0.00";

    return number.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatCompactNumber = (num) => {
    if (num === null || num === undefined) return "0.00";
  
    const number = parseFloat(num);
  
    if (isNaN(number)) return "0.00";
  
    if (number >= 10000000) {
      return (number / 10000000).toFixed(2) + "Cr";
    }
  
    if (number >= 100000) {
      return (number / 100000).toFixed(2) + "L";
    }
  
    if (number >= 1000) {
      return (number / 1000).toFixed(2) + "K";
    }
  
    return number.toFixed(2);
  };

  const formatQty = (num) => {
    if (num === null || num === undefined) return "0.00";
    const number = Number(num);
    if (!Number.isFinite(number)) return "0.00";
    return number.toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    });
  };

  const formatGreekCell = (val) => {
    if (val === null || val === undefined) return "0.0000";
    const n = Number(val);
    if (!Number.isFinite(n)) return "0.0000";
    const abs = Math.abs(n);
    if (abs >= 1000) {
      return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
    }
    if (abs >= 10) {
      return n.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 3,
      });
    }
    if (abs >= 1) {
      return n.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
    }
    if (abs >= 0.01) {
      return n.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 5,
      });
    }
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    });
  };

  const OrderType = [{ value: "Limit Order", label: tr("limitOrder") }];

  const showErrorToast = (msg) => {
    const text = String(msg || "Something went wrong");
    toast.error(text);
    if (
      /not allowed to trade in fo segment|f&o segment/i.test(text)
    ) {
      setShowFoActivationModal(true);
    }
  };
  const showSuccessToast = (msg) => toast.success(msg || "Success");
  const showWarningToast = (msg) => toast.warn(msg || "Please check");
  const pingNotification = () => {};

  const refreshHoldings = () => {};
  const updateMargin = () => {};

  const getPriceForCalculation = useCallback(() => {
    const order = buySellModalDetails.order;
    const token = Number(order?.ScripCode ?? order?.scripcode ?? 0);
    const livePrice = token ? liveByTokenRef.current[token]?.LastRate : null;
    return Number(buySellModalDetails.LimitPrice) || Number(livePrice ?? order?.CurrentPrice ?? order?.LTP ?? 0) || 0;
  }, [buySellModalDetails, orderType]);

  const getSelectedLotSize = useCallback(() => {
    const lot = Number(
      buySellModalDetails?.LotSize ??
        buySellModalDetails?.order?.LotSize ??
        buySellModalDetails?.order?.QtyLot ??
        1,
    );
    return Number.isFinite(lot) && lot > 0 ? lot : 1;
  }, [buySellModalDetails]);

  const getTotalOrderQty = useCallback(() => {
    const lots = Number(buySellModalDetails?.OrderQty || 0);
    const lotSize = getSelectedLotSize();
    return Math.max(0, Math.floor(lots * lotSize));
  }, [buySellModalDetails, getSelectedLotSize]);

  const handlePreview = useCallback(() => {
    if (!buySellModalDetails.OrderQty || buySellModalDetails.OrderQty <= 0) return;
    setIsPreview(true);
  }, [buySellModalDetails.OrderQty]);

  const handleGoBack = useCallback(() => {
    setIsPreview(false);
  }, []);

  const handleChartClick = useCallback((stock) => {
    if (!stock) return;
    setSelectedStock(stock);
  }, []);

  const openWatchlistModalForStock = useCallback(
    (stock) => {
      if (!stock) return;
      setWatchlistModalStock(stock);
    },
    [],
  );

  const SettingBuyModalDetails = useCallback((order, type) => {
    if (!order) return;
    const token = Number(order?.ScripCode ?? order?.scripcode ?? 0);
    const livePrice = token ? liveByTokenRef.current[token]?.LastRate : null;
    const basePrice = Number(livePrice ?? order?.CurrentPrice ?? order?.LTP ?? 0) || 0;
    setIsPreview(false);
    setOrderType("Limit Order");
    setBuySellModalDetails({
      FullName: order?.FullName || order?.Name || order?.Symbol || "",
      Symbol: order?.Symbol || order?.TradingSymbol || order?.FullName || "",
      Type: type === "Sell" ? "Sell" : "Buy",
      OrderQty: 1,
      LotSize: Number(order?.LotSize ?? order?.QtyLot ?? 1) || 1,
      LimitPrice: basePrice,
      order,
    });
  }, []);

  async function getCDSLRecordCount(userId) {
    try {
      const response = await fetch(
        "https://beta.infomanav.in/keep/zerodhaAPI/api.php?xAction=getCDSLRecord",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId }),
        },
      );
      if (!response.ok) return 0;
      const data = await response.json();
      return data?.count ?? 0;
    } catch (_) {
      return 0;
    }
  }

  const clickPlaceOrderPortfolio = useCallback(
    (requestData, watchlist, transactionSide, status) => {
      const executionType = orderType;
      placeOrderViaPms({
        pmsApiBaseUrl,
        sharedData,
        watchlist,
        listStatus: status,
        transactionSide,
        executionType,
        qty: getTotalOrderQty(),
        limitPrice: buySellModalDetails.LimitPrice,
      }).then((result) => {
        if (result.error) {
          showErrorToast("Failed to place order. Please try again.");
          return;
        }

        const data = result.data;
        const normalized = normalizePlaceOrderResponse(data);
        if (!result.ok) {
          const msg = normalized.message || "Failed to place order";
          showErrorToast(msg);
          return;
        }

        const message = normalized.message || "";
        const rmsResponseCode = normalized.rmsResponseCode;
        const brokerOrderID = normalized.brokerOrderID;
        const exchangeOrderID = normalized.exchangeOrderID;
        if (
          isFivePaisaBroker(sharedData) &&
          (rmsResponseCode === -162 ||
            message.toLowerCase().includes("sell authorization not received") ||
            message.toLowerCase().includes("authorization not received"))
        ) {
          showErrorToast("CDSL authorization required to place sell orders");
          setTimeout(() => document.getElementById("openCDSLModalBtn")?.click(), 500);
          return;
        }

        if (normalized.headStatus !== "0") {
          showErrorToast(message || "Failed to place order");
          return;
        }

        runPlaceOrderAcceptedFlow({
          transactionSide,
          tr,
          showSuccessToast,
          navigate,
        });
      });
    },
    [
      buySellModalDetails.LimitPrice,
      buySellModalDetails.OrderQty,
      getTotalOrderQty,
      navigate,
      orderType,
      pmsApiBaseUrl,
      sharedData,
      showSuccessToast,
      tr,
    ],
  );

  const fetchDataPortfolio = useCallback(
    (watchlist, transactionSide, status) => {
      console.log("fetchDataPortfolio", watchlist);
      
      const Exch = watchlist.Exch;
      const ExchType = watchlist.ExchType;
      const Name = watchlist.FullName || watchlist.name || watchlist.Symbol || "";
      const Scripcode =
        status === "Portfolio"
          ? Number(watchlist.ScripCode) || Number(watchlist.scripcode)
          : watchlist.Scripcode;

      const requestData = {
        ExchType,
        order_type: transactionSide,
        Exch,
        Name,
        Scripcode,
        watchlist_price: String(buySellModalDetails.LimitPrice || 0),
        min_qty: String(getTotalOrderQty()),
        userId: sharedData?.ID,
      };

      if (transactionSide === "Sell") {
        getCDSLRecordCount(sharedData?.ID)
          .then((count) => {
            if (count === 0) {
              showErrorToast("CDSL authorization required to place sell orders");
              return;
            }
            clickPlaceOrderPortfolio(requestData, watchlist, transactionSide, status);
          })
          .catch(() =>
            clickPlaceOrderPortfolio(requestData, watchlist, transactionSide, status),
          );
      } else if (transactionSide === "Buy") {
        clickPlaceOrderPortfolio(requestData, watchlist, transactionSide, status);
      }
    },
    [
      buySellModalDetails.LimitPrice,
      buySellModalDetails.OrderQty,
      clickPlaceOrderPortfolio,
      getTotalOrderQty,
      orderType,
      sharedData,
    ],
  );

  const applyLiveTick = useCallback((tick) => {
    if (!tick) return;
    const token = Number(tick.Token);
    if (!Number.isFinite(token) || token <= 0) return;

    const lastRate = tick.LastRate;
    if (lastRate === undefined || lastRate === null) return;

    const pClose = Number(tick.PClose);
    let chgPcnt = Number(tick.ChgPcnt);
    if (!Number.isFinite(chgPcnt) && Number.isFinite(pClose) && pClose !== 0) {
      chgPcnt = ((Number(lastRate) - pClose) / pClose) * 100;
    }

    liveByTokenRef.current[token] = {
      LastRate: Number(lastRate),
      ChgPcnt: Number.isFinite(chgPcnt) ? chgPcnt : null,
      PClose: Number.isFinite(pClose) ? pClose : null,
      OpenInterest: Number(tick.OpenInterest),
      Volume: Number(tick.Volume),
    };
    if (spotTokenRef.current && token === spotTokenRef.current) {
      setSpotLtp(Number(lastRate));
    }
    if (futuresTokenRef.current && token === futuresTokenRef.current) {
      setFuturesLtp(Number(lastRate));
    }
    setTimeout(() => {
      setIsLiveLoading(false);
    }, 1500);
    setLiveTickVersion((v) => v + 1);
  }, []);

  const applyGreekMessage = useCallback((parsed) => {
    if (!parsed || typeof parsed !== "object") return;
    const token = Number(parsed.Token);
    if (!Number.isFinite(token) || token <= 0) return;

    const packetId = parsed.PacketIdentifier;
    const prev = optionGreeksByTokenRef.current[token] || {};
    let next;

    if (packetId === "G") {
      next = { ...parsed };
    } else if (packetId === "I" || packetId === "O") {
      next = {
        ...prev,
        ...parsed,
        IV: parsed.IV !== undefined && parsed.IV !== null ? parsed.IV : prev.IV,
      };
    } else if (packetId === "T") {
      next = {
        ...prev,
        ...parsed,
        TWAPIV:
          parsed.TWAPIV !== undefined && parsed.TWAPIV !== null
            ? parsed.TWAPIV
            : prev.TWAPIV,
      };
    } else {
      next = { ...prev, ...parsed };
    }

    optionGreeksByTokenRef.current = {
      ...optionGreeksByTokenRef.current,
      [token]: next,
    };
    setGreeksTickVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (activeBrokerFlag !== "5paisa") {
      return undefined;
    }

    wsPriceUnsubRef.current = fivePaisaWebSocketService.onPrice((tick) => {
      applyLiveTick(tick);
    });

    wsErrorUnsubRef.current = fivePaisaWebSocketService.onError((err) => {
      console.error("[FNO] WS error:", err);
    });

    fivePaisaWebSocketService.connect();

    return () => {
      if (wsPriceUnsubRef.current) wsPriceUnsubRef.current();
      if (wsErrorUnsubRef.current) wsErrorUnsubRef.current();
      wsPriceUnsubRef.current = null;
      wsErrorUnsubRef.current = null;
    };
  }, [activeBrokerFlag, applyLiveTick]);

  useEffect(() => {
    if (activeBrokerFlag !== "5paisa") return;

    const prev = subscribedFeedItemsRef.current || [];
    const nextMap = new Map();
    displayRows.forEach((row) => {
      rowToFeedItems(row).forEach((item) => {
        nextMap.set(feedKey(item), item);
      });
    });
    const next = Array.from(nextMap.values());

    const prevKeys = new Set(prev.map(feedKey));
    const nextKeys = new Set(next.map(feedKey));

    const toUnsub = prev.filter((p) => !nextKeys.has(feedKey(p)));
    const toSub = next.filter((n) => !prevKeys.has(feedKey(n)));

    if (toUnsub.length) {
      fivePaisaWebSocketService.unsubscribeMarketFeed(toUnsub);
    }
    if (toSub.length) {
      fivePaisaWebSocketService.subscribeMarketFeed(toSub);
    }

    subscribedFeedItemsRef.current = next;

    return () => {
      const cur = subscribedFeedItemsRef.current || [];
      if (cur.length) {
        fivePaisaWebSocketService.unsubscribeMarketFeed(cur);
      }
      subscribedFeedItemsRef.current = [];
    };
  }, [activeBrokerFlag, displayRows]);

  useEffect(() => {
    if (activeBrokerFlag !== "5paisa") {
      fivePaisaOptionGreeksWebSocketService.disconnect({ manual: true });
      return undefined;
    }
    if (!greeksEnabled) {
      fivePaisaOptionGreeksWebSocketService.disconnect({ manual: true });
      return undefined;
    }

    wsGreeksUnsubRef.current = fivePaisaOptionGreeksWebSocketService.onGreeks(
      applyGreekMessage,
    );
    wsGreeksErrorUnsubRef.current = fivePaisaOptionGreeksWebSocketService.onError(
      (err) => {
        console.error("[FNO] greeks WS error:", err);
      },
    );
    fivePaisaOptionGreeksWebSocketService.connect();

    return () => {
      if (wsGreeksUnsubRef.current) {
        wsGreeksUnsubRef.current();
        wsGreeksUnsubRef.current = null;
      }
      if (wsGreeksErrorUnsubRef.current) {
        wsGreeksErrorUnsubRef.current();
        wsGreeksErrorUnsubRef.current = null;
      }
    };
  }, [activeBrokerFlag, greeksEnabled, applyGreekMessage]);

  useEffect(() => {
    if (activeBrokerFlag !== "5paisa") {
      const cur = subscribedGreekIdsRef.current || [];
      if (cur.length) {
        fivePaisaOptionGreeksWebSocketService.unsubscribeOptionGreek(cur);
        subscribedGreekIdsRef.current = [];
      }
      return undefined;
    }

    if (!greeksEnabled) {
      const cur = subscribedGreekIdsRef.current || [];
      if (cur.length) {
        fivePaisaOptionGreeksWebSocketService.unsubscribeOptionGreek(cur);
        subscribedGreekIdsRef.current = [];
      }
      return undefined;
    }

    const prev = subscribedGreekIdsRef.current || [];
    const next = displayRowsToGreekInstrumentIds(displayRows);
    const prevSet = new Set(prev);
    const nextSet = new Set(next);
    const toUnsub = prev.filter((id) => !nextSet.has(id));
    const toSub = next.filter((id) => !prevSet.has(id));

    if (toUnsub.length) {
      fivePaisaOptionGreeksWebSocketService.unsubscribeOptionGreek(toUnsub);
    }
    if (toSub.length) {
      fivePaisaOptionGreeksWebSocketService.subscribeOptionGreek(toSub);
    }
    subscribedGreekIdsRef.current = next;

    return () => {
      const cur = subscribedGreekIdsRef.current || [];
      if (cur.length) {
        fivePaisaOptionGreeksWebSocketService.unsubscribeOptionGreek(cur);
      }
      subscribedGreekIdsRef.current = [];
    };
  }, [activeBrokerFlag, displayRows, greeksEnabled]);

  useEffect(() => {
    if (activeBrokerFlag !== "5paisa") return;

    const getVisibleStocks = () => {
      const map = new Map();
      displayRows.forEach((row) => {
        [row.ce, row.pe].forEach((leg) => {
          if (!leg) return;
          const scripCode = Number(leg.ScripCode);
          if (!scripCode) return;
          const stock = {
            Exchange: toMarketFeedExchange(leg.Exch),
            ExchangeType: toMarketFeedExchangeType(leg.ExchType),
            ScripCode: String(scripCode),
          };
          if (!stock.Exchange || !stock.ExchangeType) return;
          map.set(`${stock.Exchange}|${stock.ExchangeType}|${stock.ScripCode}`, stock);
        });
      });
      return Array.from(map.values());
    };

    const pollSnapshot = async () => {
      try {
        const mergeKey = (stock) =>
          `${stock.Exchange}|${stock.ExchangeType}|${stock.ScripCode}`;
        const stockMap = new Map();
        getVisibleStocks().forEach((s) => stockMap.set(mergeKey(s), s));

        if (!isMcxChain && spotData?.ScripCode) {
          const scripCode = Number(spotData.ScripCode);
          if (scripCode) {
            const s = {
              Exchange: toMarketFeedExchange(spotData.Exch),
              ExchangeType: "C",
              ScripCode: String(scripCode),
            };
            stockMap.set(mergeKey(s), s);
          }
        }
        if (underlyingContract?.ScripCode) {
          const scripCode = Number(underlyingContract.ScripCode);
          if (scripCode) {
            const s = {
              Exchange: toMarketFeedExchange(underlyingContract.Exch),
              ExchangeType: toMarketFeedExchangeType(underlyingContract.ExchType),
              ScripCode: String(scripCode),
            };
            stockMap.set(mergeKey(s), s);
          }
        }

        const stocks = Array.from(stockMap.values());
        if (!stocks.length) return;
        const resp = await marketSnapshotService.getMarketSnapshot(stocks);
        const wrappers = Array.isArray(resp?.data) ? resp.data : [];
        const fromWrappedArray = wrappers.flatMap((entry) => {
          const rows = entry?.body?.Data;
          return Array.isArray(rows) ? rows : [];
        });
        const list =
          fromWrappedArray.length
            ? fromWrappedArray
            : resp?.data?.body?.Data ||
              resp?.data?.Data ||
              resp?.Data ||
              [];
        if (!Array.isArray(list)) return;

        const next = { ...snapshotByTokenRef.current };
        list.forEach((item) => {
          const token = Number(item?.ScripCode ?? item?.Token ?? item?.scripCode);
          if (!token) return;
          next[token] = item;
        });
        snapshotByTokenRef.current = next;
        setSnapshotVersion((v) => v + 1);
      } catch (err) {
        console.error("[FNO] snapshot poll error:", err);
      }
    };

    pollSnapshot();
    snapshotPollTimerRef.current = setInterval(pollSnapshot, 30000);

    return () => {
      if (snapshotPollTimerRef.current) {
        clearInterval(snapshotPollTimerRef.current);
        snapshotPollTimerRef.current = null;
      }
    };
  }, [activeBrokerFlag, displayRows, spotData, underlyingContract, isMcxChain]);

  useEffect(() => {
    if (activeBrokerFlag !== "5paisa") return;

    const prev = subscribedSpotRef.current;
    if (prev) {
      fivePaisaWebSocketService.unsubscribeMarketFeed([prev]);
      subscribedSpotRef.current = null;
    }

    if (isMcxChain) {
      setSpotLtp(null);
      spotTokenRef.current = null;
      return;
    }

    if (!spotData?.ScripCode) {
      setSpotLtp(null);
      spotTokenRef.current = null;
      return;
    }

    const scrip = Number(spotData.ScripCode);
    if (!Number.isFinite(scrip) || scrip <= 0) {
      setSpotLtp(null);
      spotTokenRef.current = null;
      return;
    }

    const next = {
      Exch: toMarketFeedExchange(spotData.Exch),
      ExchType: "C",
      ScripCode: scrip,
    };

    spotTokenRef.current = scrip;
    subscribedSpotRef.current = next;
    fivePaisaWebSocketService.subscribeMarketFeed([next]);

    const cached = liveByTokenRef.current[scrip];
    if (Number.isFinite(cached?.LastRate)) {
      setSpotLtp(Number(cached.LastRate));
    } else {
      setSpotLtp(null);
    }

    return () => {
      if (subscribedSpotRef.current) {
        fivePaisaWebSocketService.unsubscribeMarketFeed([subscribedSpotRef.current]);
        subscribedSpotRef.current = null;
      }
    };
  }, [activeBrokerFlag, spotData, isMcxChain]);

  useEffect(() => {
    if (activeBrokerFlag !== "5paisa") return;

    const prev = subscribedFuturesRef.current;
    if (prev) {
      fivePaisaWebSocketService.unsubscribeMarketFeed([prev]);
      subscribedFuturesRef.current = null;
    }

    if (!underlyingContract) {
      setFuturesLtp(null);
      futuresTokenRef.current = null;
      return;
    }

    const next = {
      Exch: toMarketFeedExchange(underlyingContract.Exch),
      ExchType: toMarketFeedExchangeType(underlyingContract.ExchType),
      ScripCode: Number(underlyingContract.ScripCode),
    };

    if (!next.ScripCode) {
      setFuturesLtp(null);
      futuresTokenRef.current = null;
      return;
    }

    futuresTokenRef.current = next.ScripCode;
    subscribedFuturesRef.current = next;
    fivePaisaWebSocketService.subscribeMarketFeed([next]);

    const cachedFut = liveByTokenRef.current[next.ScripCode];
    if (Number.isFinite(cachedFut?.LastRate)) {
      setFuturesLtp(Number(cachedFut.LastRate));
    } else {
      setFuturesLtp(null);
    }

    return () => {
      if (subscribedFuturesRef.current) {
        fivePaisaWebSocketService.unsubscribeMarketFeed([subscribedFuturesRef.current]);
        subscribedFuturesRef.current = null;
      }
    };
  }, [activeBrokerFlag, underlyingContract]);

  useEffect(() => {
    if (!shouldAutoScrollToAtmRef.current) return;
    if (!displayRows.length) return;
    if (!displayRows.some((row) => row.isATM)) return;

    const container = scrollContainerRef.current;
    const atmRow = atmRowRef.current;
    if (!container || !atmRow) return;

    requestAnimationFrame(() => {
      const targetTop =
        atmRow.offsetTop - container.clientHeight / 2 + atmRow.clientHeight / 2;
      container.scrollTop = Math.max(0, targetTop);
      shouldAutoScrollToAtmRef.current = false;
    });
  }, [displayRows]);

  useEffect(() => {
    if (!pendingLiveAtmCenterRef.current) return;
    if (!Number.isFinite(atmReferencePrice)) return;
    if (!allChainRows.length) return;

    const center = getAtmIndex(allChainRows, atmReferencePrice);
    const start = Math.max(0, center - STRIKE_WINDOW_RADIUS);
    const end = Math.min(allChainRows.length, center + STRIKE_WINDOW_RADIUS + 1);

    setSliceStart(start);
    setSliceEnd(end);
    shouldAutoScrollToAtmRef.current = true;
    pendingLiveAtmCenterRef.current = false;
  }, [atmReferencePrice, allChainRows]);

  const initializeDefaultNiftyChain = async () => {
    const defaultSymbol = "NIFTY";
    setSelectedSymbol(defaultSymbol);
    setSearchText(defaultSymbol);
    setShowSearch(false);
    setSelectedExpiry("");
    setContracts([]);
    setAllChainRows([]);
    setSliceStart(0);
    setSliceEnd(0);
    setSpotLtp(null);
    setFuturesLtp(null);
    setSpotData(null);
    setIsLiveLoading(true);
    spotTokenRef.current = null;
    futuresTokenRef.current = null;
    liveByTokenRef.current = {};
    shouldAutoScrollToAtmRef.current = true;
    pendingLiveAtmCenterRef.current = true;

    // Keep symbol search list ready for dropdown UX.
    fetchSymbols(defaultSymbol);

    try {
      const expiryResponse = await fetch(`${API_BASE}?xAction=getOptionExpiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbol: defaultSymbol }),
      });
      const expiryData = await expiryResponse.json();
      if (expiryData?.status !== "success") return;

      const expiryList = expiryData?.data || [];
      setExpiries(expiryList);
      if (!expiryList.length) return;

      const firstExpiry = expiryList[0];
      setSelectedExpiry(firstExpiry);

      const chainResponse = await fetch(
        `${API_BASE}?xAction=getOptionChainContracts`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            symbol: defaultSymbol,
            expiry: firstExpiry,
          }),
        },
      );
      const chainData = await chainResponse.json();
      if (chainData?.status === "success") {
        liveByTokenRef.current = {};
        setIsLiveLoading(true);
        shouldAutoScrollToAtmRef.current = true;
        pendingLiveAtmCenterRef.current = true;
        setSpotData(chainData?.SPOT_Data || null);
        setContracts(chainData?.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSymbols = async (search = "") => {
    try {
      const response = await fetch(`${API_BASE}?xAction=searchOptionSymbols`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ search }),
      });

      const data = await response.json();

      if (data?.status !== "success") return;

      const optionData = data?.data || [];

      if (optionData.length && typeof optionData[0] === "string") {
        setSearchedSymbols(optionData);
        return;
      }
    } catch (err) {
      console.error(err);
    }
  };

  const selectSymbol = async (symbol) => {
    setSelectedSymbol(symbol);
    setSearchText(symbol);
    setShowSearch(false);
    setSelectedExpiry("");
    setContracts([]);
    setAllChainRows([]);
    setSliceStart(0);
    setSliceEnd(0);
    setSpotLtp(null);
    setFuturesLtp(null);
    setSpotData(null);
    setIsLiveLoading(true);
    spotTokenRef.current = null;
    futuresTokenRef.current = null;
    liveByTokenRef.current = {};
    shouldAutoScrollToAtmRef.current = true;
    pendingLiveAtmCenterRef.current = true;

    try {
      const response = await fetch(`${API_BASE}?xAction=getOptionExpiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symbol }),
      });

      const data = await response.json();

      if (data?.status === "success") {
        setExpiries(data?.data || []);
        if (data?.data?.length > 0) {
          setSelectedExpiry(data.data[0]);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadOptionChain = async () => {
    if (!selectedSymbol || !selectedExpiry) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}?xAction=getOptionChainContracts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          symbol: selectedSymbol,
          expiry: selectedExpiry,
        }),
      });

      const data = await response.json();

      if (data?.status === "success") {
        liveByTokenRef.current = {};
        setIsLiveLoading(true);
        shouldAutoScrollToAtmRef.current = true;
        pendingLiveAtmCenterRef.current = true;
        setSpotData(data?.SPOT_Data || null);
        setContracts(data?.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!selectedSymbol || !selectedExpiry) return;
    loadOptionChain();
  }, [selectedSymbol, selectedExpiry]);

  const onChainScroll = useCallback(() => {
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const el = scrollContainerRef.current;
      if (!el || !allChainRows.length) return;

      const { scrollTop, scrollHeight, clientHeight } = el;
      const maxScroll = Math.max(1, scrollHeight - clientHeight);
      const ratio = scrollTop / maxScroll;

      if (ratio > 0.8) {
        setSliceEnd((prevEnd) =>
          Math.min(allChainRows.length, prevEnd + SCROLL_EXTEND_STRIKES),
        );
      } else if (ratio < 0.15) {
        setSliceStart((prevStart) =>
          Math.max(0, prevStart - SCROLL_EXTEND_STRIKES),
        );
      }

      const atmEl = atmRowRef.current;
      if (!atmEl) {
        setAtmPillDock("none");
        return;
      }
      const headerHeight = el.querySelector("thead")?.offsetHeight || 0;
      const rowTop = atmEl.offsetTop - el.scrollTop;
      const rowBottom = rowTop + atmEl.offsetHeight;
      const viewportBottom = el.clientHeight;

      if (rowBottom <= headerHeight + 10) {
        setAtmPillDock((prev) => (prev === "top" ? prev : "top"));
      } else if (rowTop >= viewportBottom - 35) {
        setAtmPillDock((prev) => (prev === "bottom" ? prev : "bottom"));
      } else {
        setAtmPillDock((prev) => (prev === "none" ? prev : "none"));
      }
    });
  }, [allChainRows.length]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    const atmEl = atmRowRef.current;
    if (!el || !atmEl) {
      setAtmPillDock("none");
      return;
    }
    const headerHeight = el.querySelector("thead")?.offsetHeight || 0;
    const rowTop = atmEl.offsetTop - el.scrollTop;
    const rowBottom = rowTop + atmEl.offsetHeight;
    const viewportBottom = el.clientHeight;

    if (rowBottom <= headerHeight + 4) {
      setAtmPillDock("top");
    } else if (rowTop >= viewportBottom - 8) {
      setAtmPillDock("bottom");
    } else {
      setAtmPillDock("none");
    }
  }, [displayRows, atmReferencePrice]);

  const selectedInstrumentName = isMcxChain
    ? underlyingContract?.Name ||
      underlyingContract?.FullName ||
      underlyingContract?.Symbol ||
      selectedSymbol
    : spotData?.FullName ||
      spotData?.Name ||
      underlyingContract?.Name ||
      underlyingContract?.FullName ||
      underlyingContract?.Symbol ||
      selectedSymbol;

  /** Label + LTP shown on ATM strike pill / docked pill (MCX → nearest futures XX). */
  const chainRefLabel = isMcxChain ? "FUT" : "SPOT";
  const chainRefLtpForPill = isMcxChain ? futuresLtp : spotLtp;

  const futuresLiveForHeader =
    futuresTokenRef.current != null
      ? liveByTokenRef.current[futuresTokenRef.current]
      : null;
  const futAbsPts =
    futuresLiveForHeader &&
    Number.isFinite(futuresLiveForHeader.LastRate) &&
    Number.isFinite(futuresLiveForHeader.PClose)
      ? futuresLiveForHeader.LastRate - futuresLiveForHeader.PClose
      : null;
  const futChgPcnt =
    futuresLiveForHeader != null && Number.isFinite(futuresLiveForHeader.ChgPcnt)
      ? futuresLiveForHeader.ChgPcnt
      : null;
  const futParenDisplay =
    futAbsPts != null && Number.isFinite(futAbsPts)
      ? formatNumber(futAbsPts)
      : futChgPcnt != null
        ? `${formatNumber(futChgPcnt)}%`
        : "0.00";
  const futParenColor =
    futAbsPts != null && Number.isFinite(futAbsPts)
      ? futAbsPts > 0
        ? "#16a34a"
        : futAbsPts < 0
          ? "#dc2626"
          : "#64748b"
      : futChgPcnt != null
        ? futChgPcnt > 0
          ? "#16a34a"
          : futChgPcnt < 0
            ? "#dc2626"
            : "#64748b"
        : "#64748b";
  const atmStrike = Number(allChainRows?.[atmIndex]?.strike);
  const oiMaxBySide = (() => {
    let ceMax = 0;
    let peMax = 0;
    displayRows.forEach((row) => {
      const ceCode = Number(row?.ce?.ScripCode);
      const peCode = Number(row?.pe?.ScripCode);
      const ceLive = ceCode ? liveByTokenRef.current[ceCode] : null;
      const peLive = peCode ? liveByTokenRef.current[peCode] : null;
      const ceSnap = ceCode ? snapshotByTokenRef.current[ceCode] : null;
      const peSnap = peCode ? snapshotByTokenRef.current[peCode] : null;
      const ceOi = Number(ceSnap?.OpenInterest ?? ceLive?.OpenInterest);
      const peOi = Number(peSnap?.OpenInterest ?? peLive?.OpenInterest);
      if (Number.isFinite(ceOi) && ceOi > ceMax) ceMax = ceOi;
      if (Number.isFinite(peOi) && peOi > peMax) peMax = peOi;
    });
    return { ceMax, peMax };
  })();

  return (
    <>
      <style>
        {`
          .fno-chain-row td {
            background: #f8fbff !important;
          }
          .fno-atm-row td {
            // background: #d8e7fb !important;
            border-bottom: 3px solid rgb(180, 199, 225) !important;
          }
          td.fno-ce-itm-bg {
            background: rgba(191, 0, 0, 0.06) !important;
          }
          td.fno-pe-itm-bg {
            background: rgba(191, 0, 0, 0.06) !important;
          }
          .fno-chain-scroll {
            scrollbar-width: thin;
            scrollbar-color: rgba(36, 87, 191, 0.45) transparent;
          }
          .fno-chain-scroll::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .fno-chain-scroll::-webkit-scrollbar-track {
            background: rgba(36, 87, 191, 0.08);
            border-radius: 999px;
          }
          .fno-chain-scroll::-webkit-scrollbar-thumb {
            background: rgba(36, 87, 191, 0.45);
            border-radius: 999px;
            border: 1px solid rgba(255, 255, 255, 0.55);
          }
          .fno-chain-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(36, 87, 191, 0.75);
          }
          /* Override Header.css global table tbody tr:hover */
          .fno-chain-scroll table tbody tr:hover {
            background: none !important;
            background-color: transparent !important;
          }
          .fno-chain-scroll table tbody tr:hover td {
            background: #f8fbff !important;
            background-color: #f8fbff !important;
          }
          .fno-chain-scroll table tbody tr:hover td.fno-ce-itm-bg {
            background: rgba(191, 0, 0, 0.06) !important;
            background-color: rgba(191, 0, 0, 0.06) !important;
          }
          .fno-chain-scroll table tbody tr:hover td.fno-pe-itm-bg {
            background: rgba(191, 0, 0, 0.06) !important;
            background-color: rgba(191, 0, 0, 0.06) !important;
          }
          /* Row hover: CE / PE leg actions (overlap LTP cells; no layout shift) */
          .fno-chain-actions-bar {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            display: flex;
            align-items: center;
            gap: 5px;
            padding: 5px 7px;
            background: rgba(255, 255, 255, 1);
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.3s ease;
            z-index: 20;
            height: 100%;
          }
          .fno-chain-row:hover .fno-chain-actions-bar {
            opacity: 1;
            pointer-events: auto;
          }
          .fno-chain-actions-ce {
            right: 2px;
          }
          .fno-chain-actions-pe {
            left: 2px;
          }
          .fno-action-tooltip-wrapper {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          .fno-action-tooltip-bubble {
            position: absolute;
            left: 50%;
            bottom: calc(100% + 8px);
            transform: translateX(-50%);
            background: rgba(15, 23, 42, 0.95);
            color: #ffffff;
            font-size: 10px;
            font-weight: 600;
            line-height: 1;
            padding: 6px 8px;
            border-radius: 6px;
            white-space: nowrap;
            pointer-events: none;
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.16s ease, visibility 0.16s ease;
            z-index: 130000;
          }
          .fno-action-tooltip-wrapper:hover .fno-action-tooltip-bubble {
            opacity: 1;
            visibility: visible;
          }
          .fno-expiry-wrap,
          .fno-symbol-wrap {
            position: relative;
            display: inline-flex;
            align-items: stretch;
            border-radius: 12px;
            padding: 1px;
            background: linear-gradient(
              135deg,
              rgba(148, 163, 184, 0.45) 0%,
              rgba(203, 213, 225, 0.35) 50%,
              rgba(226, 232, 240, 0.65) 100%
            );
            box-shadow:
              0 2px 8px rgba(15, 23, 42, 0.06),
              0 1px 0 rgba(255, 255, 255, 0.8) inset;
            transition: box-shadow 0.22s ease, transform 0.22s ease;
          }
          .fno-symbol-wrap {
            width: 100%;
            display: flex;
          }
          .fno-expiry-wrap:hover,
          .fno-symbol-wrap:hover {
            box-shadow:
              0 4px 14px rgba(15, 23, 42, 0.08),
              0 1px 0 rgba(255, 255, 255, 0.85) inset;
            transform: translateY(-1px);
          }
          .fno-expiry-wrap:focus-within,
          .fno-symbol-wrap:focus-within {
            box-shadow:
              0 4px 16px rgba(15, 23, 42, 0.1),
              0 0 0 2px rgba(148, 163, 184, 0.45);
            transform: translateY(-1px);
          }
          .fno-expiry-inner,
          .fno-symbol-inner {
            position: relative;
            border-radius: 11px;
            overflow: hidden;
            background: linear-gradient(180deg, #ffffff 0%, #f8fafc 55%, #f1f5f9 100%);
          }
          .fno-expiry-inner {
            min-width: 212px;
          }
          .fno-symbol-inner {
            width: 100%;
            flex: 1;
          }
          .fno-expiry-inner::before,
          .fno-symbol-inner::before {
            content: "";
            position: absolute;
            inset: 0;
            pointer-events: none;
            background: linear-gradient(
              180deg,
              rgba(255, 255, 255, 0.85) 0%,
              rgba(255, 255, 255, 0) 42%
            );
            border-radius: 11px;
          }
          .fno-symbol-field {
            position: relative;
            width: 300px;
            max-width: 100%;
          }
          .fno-symbol-input {
            position: relative;
            z-index: 1;
            box-sizing: border-box;
            width: 100%;
            margin: 0;
            border: none;
            outline: none;
            padding: 12px 16px;
            font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
            font-size: 14px;
            font-weight: 600;
            letter-spacing: 0.02em;
            color: #0f172a;
            background: transparent;
            transition: color 0.18s ease;
          }
          .fno-symbol-input::placeholder {
            color: #94a3b8;
            font-weight: 500;
          }
          .fno-symbol-dd {
            position: absolute;
            left: 0;
            right: 0;
            top: calc(100% + 8px);
            z-index: 120;
            border-radius: 12px;
            padding: 1px;
            background: linear-gradient(
              135deg,
              rgba(148, 163, 184, 0.4) 0%,
              rgba(203, 213, 225, 0.3) 50%,
              rgba(226, 232, 240, 0.55) 100%
            );
            box-shadow:
              0 4px 18px rgba(15, 23, 42, 0.08),
              0 1px 0 rgba(255, 255, 255, 0.75) inset;
            max-height: 300px;
            overflow: hidden;
          }
          .fno-symbol-dd-inner {
            border-radius: 11px;
            background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
            max-height: 298px;
            overflow-y: auto;
            font-size: 13px;
            font-weight: 600;
          }
          .fno-symbol-dd-item {
            padding: 11px 14px;
            cursor: pointer;
            color: #0f172a;
            border-bottom: 1px solid #e2e8f0;
            transition: background 0.14s ease;
          }
          .fno-symbol-dd-item:last-child {
            border-bottom: none;
          }
          .fno-symbol-dd-item:hover {
            background: #f1f5f9;
          }
          .fno-expiry-select {
            position: relative;
            z-index: 1;
            appearance: none;
            -webkit-appearance: none;
            width: 100%;
            margin: 0;
            border: none;
            outline: none;
            cursor: pointer;
            padding: 12px 40px 12px 16px;
            font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.06em;
            color: #0f172a;
            background-color: transparent;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2.25' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 14px center;
            background-size: 16px 16px;
            transition: color 0.18s ease;
          }
          .fno-expiry-select:hover {
            color: #334155;
          }
          .fno-expiry-select:focus {
            color: #0f172a;
          }
          .fno-expiry-select option {
            font-weight: 600;
            letter-spacing: 0.04em;
            text-transform: none;
            color: #0f172a;
            background: #f8fafc;
          }
        `}
      </style>
      <Header tabName={tabName} />
      <div className="wrapper__page pb-5">
        <Sidebar />

        <div
          className="content_col"
          style={{
            minHeight: "100vh",
            padding: "12px",
            fontSize: "10px",
          }}
        >
          <div
            style={{
              padding: "20px 20px 0px 20px",
              color: "#0f172a",
              minHeight: "100vh",
              background: "#EFF5FC",
            }}
          >
            <div
              style={{
                display: "grid",
                gap: 14,
                marginBottom: 20,
                padding: "14px 16px",
                borderRadius: 14,
                border: "1px solid #c7d8ee",
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(236,243,252,0.96) 100%)",
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <h2
                    style={{
                      margin: 0,
                      color: "#0f172a",
                      fontWeight: 800,
                      letterSpacing: 0.2,
                    }}
                  >
                    Option Chain
                  </h2>
                  <div style={{ color: "#475569", fontSize: 11, marginTop: 4 }}>
                    Live F&O Option Chain
                    {activeBrokerFlag !== "5paisa" && (
                      <span
                        style={{
                          marginLeft: 8,
                          color: "#b45309",
                          background: "#fef3c7",
                          border: "1px solid #fde68a",
                          borderRadius: 999,
                          padding: "2px 8px",
                          fontSize: 9,
                          fontWeight: 700,
                        }}
                      >
                        5paisa feed required
                      </span>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  <div className="fno-symbol-field">
                    <div className="fno-symbol-wrap">
                      <div className="fno-symbol-inner">
                        <input
                          className="fno-symbol-input"
                          type="text"
                          placeholder="Search symbol…"
                          value={searchText}
                          aria-label="Search option symbol"
                          autoComplete="off"
                          onChange={(e) => {
                            setSearchText(e.target.value);
                            setShowSearch(true);
                            fetchSymbols(e.target.value);
                          }}
                          onFocus={() => setShowSearch(true)}
                        />
                      </div>
                    </div>

                    {showSearch && searchedSymbols.length > 0 && (
                      <div className="fno-symbol-dd">
                        <div className="fno-symbol-dd-inner">
                          {searchedSymbols.map((item, index) => (
                            <div
                              key={index}
                              className="fno-symbol-dd-item"
                              onClick={() => selectSymbol(item)}
                            >
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="fno-expiry-wrap">
                    <div className="fno-expiry-inner">
                      <select
                        className="fno-expiry-select"
                        value={selectedExpiry}
                        aria-label="Contract expiry"
                        onChange={(e) => {
                          setIsLiveLoading(true);
                          setSelectedExpiry(e.target.value);
                        }}
                      >
                        <option value="">Expiry</option>
                        {expiries.map((exp, index) => (
                          <option key={index} value={exp}>
                            {formatExpirySelectLabel(exp)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {selectedSymbol && (
              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                  color: "#334155",
                  fontWeight: 600,
                  fontSize: 11,
                  background: "#f8fbff",
                  border: "1px solid #d6e3f3",
                  borderRadius: 10,
                  padding: "8px 10px",
                  justifyContent:"center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    // flexDirection: "column",
                    alignItems: "center",
                    gap: 25,
                    textAlign: "center",
                  }}
                >
                  <span style={{ color: "#0f172a", fontWeight: 700 }}>
                    {selectedInstrumentName}
                  </span>
                  {!isMcxChain && (
                    <span style={{ color: "#0f172a", fontWeight: 700 }}>
                      SPOT:{" "}
                      {Number.isFinite(spotLtp) ? formatNumber(spotLtp) : "0.00"}
                    </span>
                  )}
                  <span style={{ color: "#0f172a", fontWeight: 700 }}>
                    FUT:{" "}
                    {Number.isFinite(futuresLtp) ? formatNumber(futuresLtp) : "0.00"}{" "}
                    <span style={{ color: futParenColor, fontWeight: 700 }}>
                      ({futParenDisplay})
                    </span>
                  </span>
                </div>
                {activeBrokerFlag === "5paisa" && (
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      marginLeft: 6,
                      cursor: "pointer",
                      fontWeight: 600,
                      color: "#475569",
                      userSelect: "none",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={greeksEnabled}
                      onChange={(e) => setGreeksEnabled(e.target.checked)}
                      style={{ width: 14, height: 14, accentColor: "#1d4ed8" }}
                    />
                    Greeks
                  </label>
                )}
              </div>
            )}

            <div
              style={{
                border: "1px solid #cddcf0",
                borderRadius: 10,
                overflow: "hidden",
                background: "#f8fbff",
              }}
            >
              <div
                className="fno-chain-scroll"
                ref={scrollContainerRef}
                onScroll={onChainScroll}
                style={{
                  maxHeight: "80vh",
                  overflowY: "auto",
                  overflowX: greeksEnabled ? "auto" : "hidden",
                }}
              >
                {atmPillDock !== "none" && (
                  <div
                    style={{
                      position: "absolute",
                      padding: "5px 0px",
                      left: "50%",
                      transform: "translateX(-57%)",
                      ...(atmPillDock === "top" ? { top: "450px" } : { bottom: "-200px" }),
                      zIndex: 12,
                      pointerEvents: "none",
                    }}
                  >
                    <span style={atmPillSpanStyle}>
                      {Number.isFinite(chainRefLtpForPill)
                        ? `${chainRefLabel}: ${formatNumber(chainRefLtpForPill)}`
                        : "ATM"}
                    </span>
                  </div>
                )}
                {isLiveLoading && displayRows.length > 0 ? (
                  <Placeholder.Grid
                    rows={20}
                    columns={greeksEnabled ? 18 : 8}
                    active
                    style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 12 }}
                  />
                ) : (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    minWidth: greeksEnabled ? 1100 : undefined,
                  }}
                >
                  <thead
                    style={{
                      background: "#dfeaf8",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                    }}
                  >
                    <tr>
                      <th
                        colSpan={greeksEnabled ? 8 : 3}
                        style={{
                          ...thStyle,
                          borderBottom: "none",
                          color: "#0f172a",
                          fontWeight: 800,
                          background: "#d4e4f8",
                          padding: 0,
                        }}
                      >
                        <Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Call Option (European)</b><br/>Right to buy the underlying asset at the strike price on expiry.</Tooltip>}>
                          <span style={{ cursor: "pointer", display: "block", padding: 12 }}>CE</span>
                        </Whisper>
                      </th>
                      <th
                        style={{
                          ...thStyle,
                          width: 86,
                          minWidth: 86,
                          maxWidth: 86,
                          padding: "8px 6px",
                          borderLeft: "2px solid #b7c9e2",
                          borderRight: "2px solid #b7c9e2",
                          verticalAlign: "middle",
                        }}
                        rowSpan={2}
                      >
                        <Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Strike Price</b><br/>The predetermined price at which the option can be exercised.</Tooltip>}>
                          <span style={{ cursor: "pointer" }}>STRIKE</span>
                        </Whisper>
                      </th>
                      <th
                        colSpan={greeksEnabled ? 8 : 3}
                        style={{
                          ...thStyle,
                          borderBottom: "none",
                          color: "#0f172a",
                          fontWeight: 800,
                          background: "#d4e4f8",
                          padding: 0,
                        }}
                      >
                        <Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Put Option (European)</b><br/>Right to sell the underlying asset at the strike price on expiry.</Tooltip>}>
                          <span style={{ cursor: "pointer", display: "block", padding: 12 }}>PE</span>
                        </Whisper>
                      </th>
                      <th
                        style={{ ...thStyle, width: 56, minWidth: 56, maxWidth: 56, padding: 6, verticalAlign: "middle" }}
                        rowSpan={2}
                      >
                        <Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Put-Call Ratio</b><br/>Ratio of put OI to call OI — indicates market sentiment.</Tooltip>}>
                          <span style={{ cursor: "pointer" }}>PCR</span>
                        </Whisper>
                      </th>
                    </tr>
                    <tr>
                    {greeksEnabled && (
                        <>
                          <th style={{ ...thStyle, fontSize: 9, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Implied Volatility</b><br/>Market's expectation of future price movement of the underlying.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "6px 4px" }}>IV</span></Whisper></th>
                          <th style={{ ...thStyle, fontSize: 9, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Gamma (Greek)</b><br/>Rate of change of delta per 1-point move in the underlying.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "6px 4px" }}>GAMMA</span></Whisper></th>
                          <th style={{ ...thStyle, fontSize: 9, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Vega (Greek)</b><br/>Sensitivity of option price to a 1% change in implied volatility.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "6px 4px" }}>VEGA</span></Whisper></th>
                          <th style={{ ...thStyle, fontSize: 9, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Theta (Greek)</b><br/>Daily time decay — how much value the option loses per day.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "6px 4px" }}>THETA</span></Whisper></th>
                          <th style={{ ...thStyle, fontSize: 9, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Delta (Greek)</b><br/>Change in option price for a 1-point move in the underlying.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "6px 4px" }}>DELTA</span></Whisper></th>
                        </>
                      )}
                      <th style={{ ...thStyle, width: 76, minWidth: 76, maxWidth: 76, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Volume</b><br/>Total number of contracts traded in the current session.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "8px 6px" }}>Vol</span></Whisper></th>
                      <th style={{ ...thStyle, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Open Interest</b><br/>Total number of outstanding (unsettled) option contracts.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: 12 }}>OI</span></Whisper></th>
                      <th style={{ ...thStyle, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Last Traded Price (Change %)</b><br/>Most recent trade price with % change from previous close.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: 12 }}>LTP (Chg%)</span></Whisper></th>
                      
                      
                      <th style={{ ...thStyle, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Last Traded Price (Change %)</b><br/>Most recent trade price with % change from previous close.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: 12 }}>LTP (Chg%)</span></Whisper></th>
                      <th style={{ ...thStyle, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Open Interest</b><br/>Total number of outstanding (unsettled) option contracts.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: 12 }}>OI</span></Whisper></th>
                      <th style={{ ...thStyle, width: 76, minWidth: 76, maxWidth: 76, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Volume</b><br/>Total number of contracts traded in the current session.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "8px 6px" }}>Vol</span></Whisper></th>
                      {greeksEnabled && (
                        <>
                          <th style={{ ...thStyle, fontSize: 9, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Delta (Greek)</b><br/>Change in option price for a 1-point move in the underlying.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "6px 4px" }}>DELTA</span></Whisper></th>
                          <th style={{ ...thStyle, fontSize: 9, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Theta (Greek)</b><br/>Daily time decay — how much value the option loses per day.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "6px 4px" }}>THETA</span></Whisper></th>
                          <th style={{ ...thStyle, fontSize: 9, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Vega (Greek)</b><br/>Sensitivity of option price to a 1% change in implied volatility.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "6px 4px" }}>VEGA</span></Whisper></th>
                          <th style={{ ...thStyle, fontSize: 9, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Gamma (Greek)</b><br/>Rate of change of delta per 1-point move in the underlying.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "6px 4px" }}>GAMMA</span></Whisper></th>
                          <th style={{ ...thStyle, fontSize: 9, padding: 0 }}><Whisper placement="top" trigger="hover" speaker={<Tooltip className="common__tooltip"><b>Implied Volatility</b><br/>Market's expectation of future price movement of the underlying.</Tooltip>}><span style={{ cursor: "pointer", display: "block", padding: "6px 4px" }}>IV</span></Whisper></th>
                        </>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {displayRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={greeksEnabled ? 18 : 8}
                          style={{
                            ...tdStyle,
                            padding: "22px 12px",
                            color: "#64748b",
                            fontWeight: 600,
                            marginRight: "20px",
                          }}
                          className="table-empty-state-cell"
                        >
                          <span className="table-empty-order-text">
                            No data, please select an option
                          </span>
                        </td>
                      </tr>
                    ) : (
                    displayRows.map((row, index) => {
                      const ceCode = row.ce?.ScripCode;
                      const peCode = row.pe?.ScripCode;
                      const ceLive =
                        ceCode != null ? liveByTokenRef.current[Number(ceCode)] : null;
                      const peLive =
                        peCode != null ? liveByTokenRef.current[Number(peCode)] : null;
                      const ceSnapshot =
                        ceCode != null ? snapshotByTokenRef.current[Number(ceCode)] : null;
                      const peSnapshot =
                        peCode != null ? snapshotByTokenRef.current[Number(peCode)] : null;

                      const ceGreeks =
                        greeksEnabled && ceCode != null
                          ? optionGreeksByTokenRef.current[Number(ceCode)]
                          : null;
                      const peGreeks =
                        greeksEnabled && peCode != null
                          ? optionGreeksByTokenRef.current[Number(peCode)]
                          : null;
                      const greekTdStyle = {
                        ...tdStyle,
                        fontSize: 10,
                        padding: "5px 4px",
                        whiteSpace: "nowrap",
                      };

                      const ceLtp =
                        ceLive?.LastRate != null && Number.isFinite(ceLive.LastRate)
                          ? formatNumber(ceLive.LastRate)
                          : row.ce?.ScripCode != null
                            ? "0.00"
                            : "0.00";
                      const peLtp =
                        peLive?.LastRate != null && Number.isFinite(peLive.LastRate)
                          ? formatNumber(peLive.LastRate)
                          : row.pe?.ScripCode != null
                            ? "0.00"
                            : "0.00";

                      const ceChg = ceLive?.ChgPcnt;
                      const peChg = peLive?.ChgPcnt;
                      const ceOi = Number(
                        ceSnapshot?.OpenInterest ?? ceLive?.OpenInterest,
                      );
                      const peOi = Number(
                        peSnapshot?.OpenInterest ?? peLive?.OpenInterest,
                      );
                      const ceVol = Number(ceSnapshot?.Volume ?? ceLive?.Volume);
                      const peVol = Number(peSnapshot?.Volume ?? peLive?.Volume);
                      const ceOiFillPct =
                        Number.isFinite(ceOi) && oiMaxBySide.ceMax > 0
                          ? Math.min(280, (ceOi / oiMaxBySide.ceMax) * 300)
                          : 0;
                      const peOiFillPct =
                        Number.isFinite(peOi) && oiMaxBySide.peMax > 0
                          ? Math.min(280, (peOi / oiMaxBySide.peMax) * 300)
                          : 0;
                      const isBelowAtm =
                        Number.isFinite(atmStrike) && Number(row.strike) < atmStrike;
                      const isAboveAtm =
                        Number.isFinite(atmStrike) && Number(row.strike) > atmStrike;
                      const ceItmClass = isBelowAtm ? "fno-ce-itm-bg" : "";
                      const peItmClass = isAboveAtm ? "fno-pe-itm-bg" : "";
                      const pcr =
                        Number.isFinite(ceOi) && ceOi > 0 && Number.isFinite(peOi)
                          ? peOi / ceOi
                          : null;
                      return (
                        <tr
                          key={`${row.strike}-${sliceStart + index}`}
                          ref={row.isATM ? atmRowRef : null}
                          className={`fno-chain-row ${row.isATM ? "fno-atm-row" : ""}`}
                        >



                           {greeksEnabled && (
                            <>
                              <td className={ceItmClass} style={greekTdStyle}>
                                {formatGreekCell(ceGreeks?.IV)}
                              </td>
                              <td className={ceItmClass} style={greekTdStyle}>
                                {formatGreekCell(ceGreeks?.GAMMA)}
                              </td>
                              <td className={ceItmClass} style={greekTdStyle}>
                                {formatGreekCell(ceGreeks?.VEGA)}
                              </td>
                              <td className={ceItmClass} style={greekTdStyle}>
                                {formatGreekCell(ceGreeks?.THETA)}
                              </td>
                              <td className={ceItmClass} style={greekTdStyle}>
                                {formatGreekCell(ceGreeks?.DELTA)}
                              </td>
                            </>
                          )}



                        {/* Volume CE */}
                        <td
                          className={ceItmClass}
                          style={{ ...tdStyle, width: 76, minWidth: 76, maxWidth: 76, padding: "8px 6px" }}
                        >
                          {Number.isFinite(ceVol) ? formatCompactNumber(ceVol) : "0.00"}
                        </td>
                          {/* OI */}
                          <td
                            className={ceItmClass}
                            style={{ ...tdStyle, position: "relative", overflow: "visible" }}
                          >
                            {!greeksEnabled && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: 2,
                                  bottom: 2,
                                  left: 0,
                                  width: `${ceOiFillPct}%`,
                                  background: "rgba(211, 0, 32, 0.23)",
                                  borderTopRightRadius: 18,
                                  borderBottomRightRadius: 18,
                                  pointerEvents: "none",
                                  height: "22px",
                                  margin: "auto",
                                  zIndex: 1,
                                }}
                              />
                            )}
                            <span style={{ position: "relative", zIndex: 1 }}>
                              {Number.isFinite(ceOi) ? formatCompactNumber(ceOi) : "0.00"}
                            </span>
                          </td>

                          {/* Live Price CE + hover actions (overlap toward strike) */}
                          <td
                            className={ceItmClass}
                            style={{
                              ...tdStyle,
                              position: "relative",
                              overflow: "visible",
                              verticalAlign: "middle",
                              color:
                                ceChg == null
                                  ? undefined
                                  : ceChg > 0
                                    ? "#22c55e"
                                    : ceChg < 0
                                      ? "#ef4444"
                                      : "#64748b",
                            }}
                          >
                            <span style={{ fontWeight: 700, color: "#0f172a", paddingRight: "5px" }}>
                              {ceLtp}
                            </span>
                            <span style={{ fontWeight: 700 }}>
                              ({" "}
                              {ceChg != null && Number.isFinite(ceChg)
                                ? `${formatNumber(ceChg)}%`
                                : row.ce
                                  ? "0.00"
                                  : "0.00"}
                              )
                            </span>
                            {row.ce && (
                              <div
                                className="fno-chain-actions-bar fno-chain-actions-ce"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                role="toolbar"
                                aria-label="Call leg actions"
                                data-leg="CE"
                                data-strike={row.strike}
                              >
                                <span className="fno-action-tooltip-wrapper">
                                  <button
                                    type="button"
                                    title="Buy"
                                    data-bs-toggle="modal"
                                    data-bs-target="#BuySellModal"
                                    onClick={() => SettingBuyModalDetails(row.ce, "Buy")}
                                    style={{
                                      ...fnoLegActionBtnBase,
                                      width: 26,
                                      height: 26,
                                      background: "#f0fdf4",
                                      color: "#047857",
                                      fontWeight: 800,
                                      fontSize: 11,
                                      zIndex: 1,
                                    }}
                                  >
                                    B
                                  </button>
                                  <span className="fno-action-tooltip-bubble">Buy</span>
                                </span>
                                <span className="fno-action-tooltip-wrapper">
                                  <button
                                    type="button"
                                    title="Sell"
                                    data-bs-toggle="modal"
                                    data-bs-target="#BuySellModal"
                                    onClick={() => SettingBuyModalDetails(row.ce, "Sell")}
                                    style={{
                                      ...fnoLegActionBtnBase,
                                      width: 26,
                                      height: 26,
                                      background: "#fff5f5",
                                      color: "#be123c",
                                      fontWeight: 800,
                                      fontSize: 11,
                                    }}
                                  >
                                    S
                                  </button>
                                  <span className="fno-action-tooltip-bubble">Sell</span>
                                </span>
                                <span className="fno-action-tooltip-wrapper">
                                  <button
                                    type="button"
                                    title="Chart"
                                    aria-label="Chart"
                                    onClick={() => handleChartClick(row.ce)}
                                    style={{
                                      ...fnoLegActionBtnBase,
                                      width: 28,
                                      height: 28,
                                      background: "transparent",
                                      color: "#60a5fa",
                                      padding: 0,
                                    }}
                                  >
                                    <FiTrendingUp size={16} strokeWidth={2.25} />
                                  </button>
                                  <span className="fno-action-tooltip-bubble">Chart</span>
                                </span>
                                <span className="fno-action-tooltip-wrapper">
                                  <button
                                    type="button"
                                    title="Watchlist"
                                    aria-label="Watchlist"
                                    onClick={() => openWatchlistModalForStock(row.ce)}
                                    style={{
                                      ...fnoLegActionBtnBase,
                                      width: 28,
                                      height: 28,
                                      background: "transparent",
                                      color: "#60a5fa",
                                      padding: 0,
                                    }}
                                  >
                                    <FiBookmark size={16} strokeWidth={2.25} />
                                  </button>
                                  <span className="fno-action-tooltip-bubble">Watchlist</span>
                                </span>
                                {/* <span className="fno-action-tooltip-wrapper">
                                  <button
                                    type="button"
                                    title="Basket"
                                    aria-label="Basket"
                                    style={{
                                      ...fnoLegActionBtnBase,
                                      width: 28,
                                      height: 28,
                                      background: "transparent",
                                      color: "#60a5fa",
                                      padding: 0,
                                    }}
                                  >
                                    <FiShoppingBag size={16} strokeWidth={2.25} />
                                  </button>
                                  <span className="fno-action-tooltip-bubble">Basket</span>
                                </span> */}
                              </div>
                            )}
                          </td>
                        
                          
                          {/* Strike */}
                          <td
  style={{
    ...tdStyle,
    width: 86,
    minWidth: 86,
    maxWidth: 86,
    padding: "8px 6px",
    fontWeight: 700,
    color: row.isATM ? "#1d4ed8" : "#334155",
    position: "relative",
    paddingBottom: tdStyle.padding,
    borderLeft: "2px solid #d1dded",
    borderRight: "2px solid #d1dded",
  }}
>
  {/* {console.log("ROW:", row)} */}

  <span>{formatNumber(row.strike)}</span>

  {row.isATM && (
    <span
      style={{
        ...atmPillSpanStyle,
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: "-9px",
        zIndex: 5,
      }}
    >
      {Number.isFinite(chainRefLtpForPill)
        ? `${chainRefLabel}: ${formatNumber(chainRefLtpForPill)}`
        : "ATM"}
    </span>
  )}
</td>

                        
                         
                          {/* Live Price PE + hover actions (overlap toward strike) */}
                          <td
                            className={peItmClass}
                            style={{
                              ...tdStyle,
                              position: "relative",
                              overflow: "visible",
                              verticalAlign: "middle",
                              color:
                                peChg == null
                                  ? undefined
                                  : peChg > 0
                                    ? "#22c55e"
                                    : peChg < 0
                                      ? "#ef4444"
                                      : "#64748b",
                            }}
                          >
                            <span style={{ fontWeight: 700, color: "#0f172a", paddingRight: "5px" }}>
                              {peLtp}
                            </span>
                            <span style={{ fontWeight: 700 }}>
                              ({" "}
                              {peChg != null && Number.isFinite(peChg)
                                ? `${formatNumber(peChg)}%`
                                : row.pe
                                  ? "0.00"
                                  : "0.00"}
                              )
                            </span>
                            {row.pe && (
                              <div
                                className="fno-chain-actions-bar fno-chain-actions-pe"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                role="toolbar"
                                aria-label="Put leg actions"
                                data-leg="PE"
                                data-strike={row.strike}
                              >
                                <span className="fno-action-tooltip-wrapper">
                                  <button
                                    type="button"
                                    title="Buy"
                                    data-bs-toggle="modal"
                                    data-bs-target="#BuySellModal"
                                    onClick={() => SettingBuyModalDetails(row.pe, "Buy")}
                                    style={{
                                      ...fnoLegActionBtnBase,
                                      width: 26,
                                      height: 26,
                                      background: "#f0fdf4",
                                      color: "#047857",
                                      fontWeight: 800,
                                      fontSize: 11,
                                    }}
                                  >
                                    B
                                  </button>
                                  <span className="fno-action-tooltip-bubble">Buy</span>
                                </span>
                                <span className="fno-action-tooltip-wrapper">
                                  <button
                                    type="button"
                                    title="Sell"
                                    data-bs-toggle="modal"
                                    data-bs-target="#BuySellModal"
                                    onClick={() => SettingBuyModalDetails(row.pe, "Sell")}
                                    style={{
                                      ...fnoLegActionBtnBase,
                                      width: 26,
                                      height: 26,
                                      background: "#fff5f5",
                                      color: "#be123c",
                                      fontWeight: 800,
                                      fontSize: 11,
                                    }}
                                  >
                                    S
                                  </button>
                                  <span className="fno-action-tooltip-bubble">Sell</span>
                                </span>
                                <span className="fno-action-tooltip-wrapper">
                                  <button
                                    type="button"
                                    title="Chart"
                                    aria-label="Chart"
                                    onClick={() => handleChartClick(row.pe)}
                                    style={{
                                      ...fnoLegActionBtnBase,
                                      width: 28,
                                      height: 28,
                                      background: "transparent",
                                      color: "#60a5fa",
                                      padding: 0,
                                    }}
                                  >
                                    <FiTrendingUp size={16} strokeWidth={2.25} />
                                  </button>
                                  <span className="fno-action-tooltip-bubble">Chart</span>
                                </span>
                                <span className="fno-action-tooltip-wrapper">
                                  <button
                                    type="button"
                                    title="Watchlist"
                                    aria-label="Watchlist"
                                    onClick={() => openWatchlistModalForStock(row.pe)}
                                    style={{
                                      ...fnoLegActionBtnBase,
                                      width: 28,
                                      height: 28,
                                      background: "transparent",
                                      color: "#60a5fa",
                                      padding: 0,
                                    }}
                                  >
                                    <FiBookmark size={16} strokeWidth={2.25} />
                                  </button>
                                  <span className="fno-action-tooltip-bubble">Watchlist</span>
                                </span>
                                {/* <span className="fno-action-tooltip-wrapper">
                                  <button
                                    type="button"
                                    title="Basket"
                                    aria-label="Basket"
                                    style={{
                                      ...fnoLegActionBtnBase,
                                      width: 28,
                                      height: 28,
                                      background: "transparent",
                                      color: "#60a5fa",
                                      padding: 0,
                                    }}
                                  >
                                    <FiShoppingBag size={16} strokeWidth={2.25} />
                                  </button>
                                  <span className="fno-action-tooltip-bubble">Basket</span>
                                </span> */}
                              </div>
                            )}
                          </td>

                           {/* OI */}  
                          <td
                            className={peItmClass}
                            style={{ ...tdStyle, position: "relative", overflow: "visible" }}
                          >
                            {!greeksEnabled && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: 2,
                                  bottom: 2,
                                  right: 0,
                                  width: `${peOiFillPct}%`,
                                  background: "rgba(0, 193, 80, 0.31)",
                                  borderTopLeftRadius: 18,
                                  borderBottomLeftRadius: 18,
                                  pointerEvents: "none",
                                  height: "22px",
                                  margin: "auto",
                                }}
                              />
                            )}
                            <span style={{ position: "relative", zIndex: 1 }}>
                              {Number.isFinite(peOi) ? formatCompactNumber(peOi) : "0.00"}
                            </span>
                            </td>
                          {/* Volume */}
                          <td
                            className={peItmClass}
                            style={{ ...tdStyle, width: 76, minWidth: 76, maxWidth: 76, padding: "8px 6px" }}
                          >
                            {Number.isFinite(peVol) ? formatCompactNumber(peVol) : "0.00"}
                          </td>


                          {greeksEnabled && (
                            <>
                              <td className={peItmClass} style={greekTdStyle}>
                                {formatGreekCell(peGreeks?.DELTA)}
                              </td>
                              <td className={peItmClass} style={greekTdStyle}>
                                {formatGreekCell(peGreeks?.THETA)}
                              </td>
                              <td className={peItmClass} style={greekTdStyle}>
                                {formatGreekCell(peGreeks?.VEGA)}
                              </td>
                              <td className={peItmClass} style={greekTdStyle}>
                                {formatGreekCell(peGreeks?.GAMMA)}
                              </td>
                              <td className={peItmClass} style={greekTdStyle}>
                                {formatGreekCell(peGreeks?.IV)}
                              </td>
                            </>
                          )}




                          {/* PCR */}
                          <td style={{ ...tdStyle, width: 56, minWidth: 56, maxWidth: 56, padding: 6 }} className={peItmClass}>
                            {Number.isFinite(pcr) ? formatNumber(pcr) : "0.00"}
                          </td>
                        </tr>
                      );
                    }))}
                  </tbody>
                </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showFoActivationModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.5)",
            zIndex: 200000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => setShowFoActivationModal(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 460,
              background: "#ffffff",
              borderRadius: 14,
              border: "1px solid #dbe6f5",
              boxShadow: "0 18px 48px rgba(15, 23, 42, 0.28)",
              padding: 20,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4
              style={{
                margin: 0,
                marginBottom: 8,
                color: "#0f172a",
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              Derivatives Segment Required
            </h4>
            <p
              style={{
                margin: 0,
                marginBottom: 16,
                color: "#475569",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              F&O segment not activated for this account.
              <br />
              Please enable Derivatives trading in 5paisa.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowFoActivationModal(false)}
                style={{
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#334155",
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowFoActivationModal(false);
                  window.open(
                    "https://modification.5paisa.com/derivative/segment-details",
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
                style={{
                  border: "none",
                  background: "#1d4ed8",
                  color: "#ffffff",
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontWeight: 600,
                }}
              >
                Activate Now
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="modal fade modal_md modern-trading-modal"
        id="BuySellModal"
        aria-labelledby="BuySellModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-modern">
          <div className="modal-content modal-content-modern">
            <div className="modal-header modal-header-modern">
              <div className="modal-header-content">
                <div className="modal-title-wrapper">
                  <div className={`trading-type-badge ${buySellModalDetails.Type.toLowerCase()}`}>
                    <img
                      src={buySellModalDetails.Type === "Buy" ? ArrowTrendUp : ArrowTrendDown}
                      className="trading-icon"
                      alt="icon"
                    />
                    <span className="trading-type-text">{buySellModalDetails.Type}</span>
                  </div>
                  <h3 className="modal-title-modern">{buySellModalDetails.Symbol}</h3>
                </div>
                <button
                  type="button"
                  className="btn-close-modern"
                  data-bs-dismiss="modal"
                  aria-label={tr("close")}
                  onClick={() => setIsPreview(false)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>

            <div className="modal-body modal-body-modern">
              <AnimatePresence mode="wait">
                {!isPreview ? (
                  <motion.div
                    key="screen1"
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -30, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <div
                      className="sell__content"
                      style={{
                        backgroundColor: buySellModalDetails.Type === "Buy" ? "#F6FCF9" : "#fff7f7",
                        border: `1px solid ${buySellModalDetails.Type === "Buy" ? "#B5E8CF" : "#F5B5B5"}`,
                      }}
                    >
                      <div className="sell__content_inner d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-0">{buySellModalDetails.Symbol}</h5>
                          <h6 className="mb-0">{buySellModalDetails.FullName}</h6>
                        </div>
                        <p className="mb-0">
                          {(() => {
                            const order = buySellModalDetails.order;
                            const token = Number(order?.ScripCode ?? order?.scripcode ?? 0);
                            const live = token ? liveByTokenRef.current[token] : null;
                            const currentPrice =
                              Number(live?.LastRate ?? order?.CurrentPrice ?? order?.LTP ?? 0) || 0;
                            const actualChangePercent = Number(live?.ChgPcnt ?? 0) || 0;
                            const isPositive = actualChangePercent >= 0;
                            return (
                              <>
                                ₹ {formatNumber(currentPrice)} <br />
                                <span className={isPositive ? "text-success" : "text-danger"}>
                                  ({isPositive ? "+" : ""}
                                  {actualChangePercent ? actualChangePercent.toFixed(2) : "0.00"}%)
                                  <i
                                    className={`icon-arrow-${isPositive ? "up" : "down"} ms-1`}
                                    style={{ fontSize: "0.8em" }}
                                  ></i>
                                </span>
                              </>
                            );
                          })()}
                        </p>
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: "12px", fontWeight: "500", color: "#64748b", marginBottom: "6px" }}>{tr("orderType")}</label>
                        <SelectPicker
                          data={OrderType}
                          searchable={false}
                          container={document.body}
                          className="custom-select-picker"
                          value={orderType}
                          onChange={() => setOrderType("Limit Order")}
                          disabled
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontSize: "12px", fontWeight: "500", color: "#64748b", marginBottom: "6px" }}>Lots</label>
                        <input
                          type="number"
                          className="form-control"
                          min={1}
                          step={1}
                          value={buySellModalDetails.OrderQty}
                          onChange={(e) =>
                            setBuySellModalDetails((prev) => ({
                              ...prev,
                              OrderQty: Math.max(1, Number(e.target.value || 1)),
                            }))
                          }
                          style={{
                            width: "100%",
                            height: "44px",
                            borderRadius: "10px",
                            border: "1px solid #d5dfed",
                            padding: "10px 12px",
                            fontSize: "14px",
                            color: "#0f172a",
                            outline: "none",
                            boxShadow: "none",
                          }}
                        />
                      </div>
                    </div>

                    <div className="mt-3">
                      <label className="form-label" style={{ fontSize: "12px", fontWeight: "500", color: "#64748b", marginBottom: "6px" }}>{tr("limitPrice")}</label>
                      <input
                        type="number"
                        className="form-control"
                        value={buySellModalDetails.LimitPrice}
                        onChange={(e) =>
                          setBuySellModalDetails((prev) => ({
                            ...prev,
                            LimitPrice: Number(e.target.value),
                          }))
                        }
                        style={{
                          width: "100%",
                          height: "44px",
                          borderRadius: "10px",
                          border: "1px solid #d5dfed",
                          padding: "10px 12px",
                          fontSize: "14px",
                          color: "#0f172a",
                          outline: "none",
                          boxShadow: "none",
                        }}
                      />
                    </div>

                    {buySellModalDetails.OrderQty > 0 && (
                      <motion.div
                        className="order-summary mt-4 p-3 border roundedX"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4 }}
                        style={{
                          backgroundColor: buySellModalDetails.Type === "Buy" ? "#F6FCF9" : "#fff7f7",
                          border: `1px solid ${buySellModalDetails.Type === "Buy" ? "#B5E8CF" : "#F5B5B5"}`,
                        }}
                      >
                        <h6 className="fw-bold mb-3">{tr("orderSummary")}</h6>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("orderType")}</span>
                          <span className="value">{orderType}</span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("quantity")}</span>
                          <span className="value">
                            {buySellModalDetails.OrderQty} Lots ({getTotalOrderQty()} Qty)
                          </span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("pricePerShare")}</span>
                          <span className="value">₹{formatNumber(getPriceForCalculation())}</span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("subtotal")}</span>
                          <span className="value">₹{formatNumber(getTotalOrderQty() * getPriceForCalculation())}</span>
                        </div>
                        <div className="order-row d-flex justify-content-between">
                          <span className="label">{tr("estFees")}</span>
                          <span className="value">₹0.00</span>
                        </div>
                        <hr />
                        <div className="order-total d-flex justify-content-between fw-bold">
                          <span>{tr("totalAmount")}</span>
                          <span>₹{formatNumber(getTotalOrderQty() * getPriceForCalculation())}</span>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    key="screen2"
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -30, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <div className="preview-header-modern">
                      <div className={`preview-icon-wrapper ${buySellModalDetails.Type.toLowerCase()}`}>
                        <img
                          src={buySellModalDetails.Type === "Buy" ? ArrowTrendUp : ArrowTrendDown}
                          className="preview-icon"
                          alt="icon"
                        />
                      </div>
                      <h4 className="preview-title">{tr("confirmOrder")}</h4>
                      <p className="preview-subtitle">{tr("pleaseReviewOrderDetails")}</p>
                    </div>

                    <div className="order-summary-preview">
                      <div className="preview-row">
                        <span className="preview-label">{tr("orderType")}</span>
                        <span className="preview-value">{orderType}</span>
                      </div>
                      <div className="preview-row">
                        <span className="preview-label">{tr("quantity")}</span>
                        <span className="preview-value">
                          {buySellModalDetails.OrderQty} Lots ({getTotalOrderQty()} Qty)
                        </span>
                      </div>
                      <div className="preview-row">
                        <span className="preview-label">{tr("pricePerShare")}</span>
                        <span className="preview-value">₹{formatNumber(getPriceForCalculation())}</span>
                      </div>
                      <div className="preview-row">
                        <span className="preview-label">{tr("subtotal")}</span>
                        <span className="preview-value">₹{formatNumber(getTotalOrderQty() * getPriceForCalculation())}</span>
                      </div>
                      <div className="preview-row">
                        <span className="preview-label">{tr("estFees")}</span>
                        <span className="preview-value">₹0.00</span>
                      </div>
                      <div className="preview-divider"></div>
                      <div className="preview-total">
                        <span className="preview-total-label">{tr("totalAmount")}</span>
                        <span className="preview-total-value">
                          ₹{formatNumber(getTotalOrderQty() * getPriceForCalculation())}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="modal-footer modal-footer-modern">
              {!isPreview ? (
                <div className="footer-actions" style={{ display: "flex", gap: "0.75rem" }}>
                  <button
                    type="button"
                    className="btn-modern btn-secondary-modern"
                    data-bs-dismiss="modal"
                    onClick={() => setIsPreview(false)}
                  >
                    {tr("cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn-modern"
                    onClick={handlePreview}
                    disabled={!buySellModalDetails.OrderQty || buySellModalDetails.OrderQty <= 0}
                  >
                    {tr("previewOrder")}
                  </button>
                </div>
              ) : (
                <div className="footer-actions">
                  <button
                    type="button"
                    className="btn-modern btn-secondary-modern"
                    onClick={handleGoBack}
                  >
                    {tr("goBack")}
                  </button>
                  <button
                    type="button"
                    className={`btn-modern btn-success-modern ${buySellModalDetails.Type.toLowerCase()}`}
                    onClick={(e) => {
                      fetchDataPortfolio(
                        buySellModalDetails.order,
                        buySellModalDetails.Type,
                        "Portfolio",
                      );
                      e.target.disabled = true;
                      e.target.textContent = tr("processing");
                      setTimeout(() => {
                        setIsPreview(false);
                        e.target.disabled = false;
                        e.target.textContent = tr("confirmOrder");
                      }, 1000);
                    }}
                    data-bs-dismiss="modal"
                  >
                    {tr("confirmOrder")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SelectWatchlistModal
        isOpen={!!watchlistModalStock}
        onClose={() => setWatchlistModalStock(null)}
        selectedStock={watchlistModalStock}
        pmsApiBaseUrl={pmsApiBaseUrl}
        sharedData={sharedData}
      />

      <StockCandleModal
        isOpen={!!selectedStock}
        onClose={() => setSelectedStock(null)}
        Exch={selectedStock?.Exch}
        ExchType={selectedStock?.ExchType}
        ScripCode={selectedStock?.ScripCode}
        Name={selectedStock?.FullName || selectedStock?.name}
        Symbol={selectedStock?.Symbol || selectedStock?.name}
      />
    </>
  );
}

export default FNO;
